import { MouseEvent, memo, useCallback, useMemo } from 'react';

import {
  DeleteFilled,
  DownloadOutlined,
  EditFilled,
  ExclamationCircleOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { Modal, Space, Table, Tooltip, Typography, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { History } from 'history';
import { Link, useLocation, withRouter } from 'react-router-dom';
import { useTableUrlSync } from '../../../../../../../hooks/useTableUrlSync';

import { useAuth } from '../../../../../../../hooks/useAuth';
import { PlanogramModel } from '../../../../../../../models/planogram';
import { UserModel } from '../../../../../../../models/user';
import { useAppDispatch, useAppSelector } from '../../../../../../../store';
import { deletePlanogram } from '../../../../../../../store/features/planogram/planogramSlice';
// import { ListModelParams } from '../../../../../../../types';
import {
  UserPermissions,
  formatDate,
  propsAreEqual,
} from '../../../../../../../util';
import { DrawerHashRoute } from '../../../../../../containers/Drawers/types';

const { Text } = Typography;

interface PlanogramTableListProps {
  projects: PlanogramModel[];
  loading: boolean;
  history: History;
}

const PlanogramTableList = ({
  projects,
  loading,
  history,
}: PlanogramTableListProps) => {
  const { hasPermission } = useAuth();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { totalCount, page, pageSize } = useAppSelector(
    (state) => state.planogram
  );

  const SORT_FIELD_MAP: Record<string, string> = useMemo(
    () => ({ updated_at: 'updated_at', name: 'name' }),
    []
  );

  const { currentSortField, currentSortOrder, onTableChange } = useTableUrlSync(
    {
      location,
      history,
      page,
      pageSize,
      sortFieldMap: SORT_FIELD_MAP,
    }
  );

  const handleSendToOVR = useCallback(
    (e: MouseEvent, project: PlanogramModel) => {
      e.preventDefault();
      console.log('send to OVR ', project);
      // Implement send to OVR functionality
      message.info('Send to OVR functionality to be implemented');
    },
    []
  );

  const handleDownloadProject = useCallback(
    (e: MouseEvent, project: PlanogramModel) => {
      e.preventDefault();
      message.success(`Downloading project: ${project?.name}`);
    },
    []
  );

  const handleDeleteProject = useCallback(
    async (project: PlanogramModel) => {
      try {
        message.loading(
          { content: 'Deleting project...', key: 'deleting-project-message' },
          0
        );

        await dispatch(deletePlanogram(project.uuid)).unwrap();
        message.success({
          content: `Project '${project?.name}' deleted.`,
          key: 'deleting-project-message',
        });
      } catch (error) {
        message.error({
          content: 'Failed to delete project',
          key: 'deleting-project-message',
        });
      }
    },
    [dispatch]
  );

  const handleConfirmDeleteProject = useCallback(
    (e: MouseEvent, project: PlanogramModel) => {
      e.preventDefault();
      Modal.confirm({
        title: `Delete project '${project?.name}'?`,
        icon: <ExclamationCircleOutlined />,
        content: 'You will not be able to recover it.',
        okText: 'Delete',
        onOk: () => handleDeleteProject(project),
      });
    },
    [handleDeleteProject]
  );

  const columns: ColumnsType<PlanogramModel> = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name, project) => {
          return (
            <Link to={`/planogram/${project.uuid}`}>
              <Text style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
                {name}
              </Text>
            </Link>
          );
        },
        sorter: true,
        sortOrder: currentSortField === 'name' ? currentSortOrder : null,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (description: string) =>
          !!description ? (
            <Tooltip title={description} mouseEnterDelay={1}>
              <Text ellipsis style={{ maxWidth: '400px' }}>
                {description}
              </Text>
            </Tooltip>
          ) : (
            <Text type="secondary" style={{ fontStyle: 'italic' }}>
              No description.
            </Text>
          ),
      },
      {
        title: 'Last updated',
        dataIndex: 'updated_at',
        key: 'updated_at',
        render: (date: string) => {
          return <div>{date ? formatDate(date) : null}</div>;
        },
        sorter: true,
        sortOrder: currentSortField === 'updated_at' ? currentSortOrder : null,
      },
      {
        title: 'Created by',
        dataIndex: 'createdByUser',
        key: 'createdByUser',
        render: (user: UserModel) => user?.name,
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (project: PlanogramModel) => (
          <Space direction="horizontal" size="middle">
            <Tooltip title="Send to OVR">
              <ExportOutlined
                onClick={(e) => handleSendToOVR(e, project)}
                className="planogram-project-table-list-action-icon"
              />
            </Tooltip>
            <Tooltip title="Download">
              <DownloadOutlined
                onClick={(e) => handleDownloadProject(e, project)}
                className="planogram-project-table-list-action-icon"
              />
            </Tooltip>
            {hasPermission(UserPermissions.PlanogramProjectsEdit) && (
              <Tooltip title="Edit">
                <EditFilled
                  onClick={() =>
                    history.push({
                      hash: DrawerHashRoute.PlanogramForm,
                      state: { data: project },
                    })
                  }
                  className="planogram-project-table-list-action-icon"
                />
              </Tooltip>
            )}
            {hasPermission(UserPermissions.PlanogramProjectsDelete) && (
              <Tooltip title="Delete">
                <DeleteFilled
                  onClick={(e) => handleConfirmDeleteProject(e, project)}
                  className="planogram-project-table-list-action-icon"
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [
      handleConfirmDeleteProject,
      handleSendToOVR,
      handleDownloadProject,
      hasPermission,
      history,
      currentSortField,
      currentSortOrder,
    ]
  );

  return (
    <Table
      loading={loading}
      locale={{ emptyText: <></> }}
      rowKey={(project) => `${project.uuid}-row-key`}
      sortDirections={['ascend', 'descend', 'ascend']}
      className="planogram-projects-table-list"
      onChange={onTableChange}
      pagination={{
        current: page,
        pageSize,
        total: totalCount,
      }}
      columns={columns}
      dataSource={projects}
    />
  );
};

export default withRouter<any, any>(memo(PlanogramTableList, propsAreEqual));
